import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Spinner,
  Badge,
} from "reactstrap";
import { updateUserReport } from "./reportedUsersApi";

const BatchUpdateUserReportsModal = ({ isOpen, toggle, selectedReports, onUpdate }) => {
  const [state, setState] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  useEffect(() => {
    if (!isOpen) {
      setState("");
      setAdminNote("");
      setProgress({ current: 0, total: 0 });
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedReports || selectedReports.length === 0) {
      alert("No reports selected for batch update.");
      return;
    }

    if (!state && (!adminNote || !adminNote.trim())) {
      alert("Please select a state or provide an admin note to update.");
      return;
    }

    setLoading(true);
    setProgress({ current: 0, total: selectedReports.length });

    try {
      // Update each report sequentially
      for (let i = 0; i < selectedReports.length; i++) {
        const report = selectedReports[i];
        setProgress({ current: i + 1, total: selectedReports.length });

        const updateData = {
          reportUuid: report.report_id,
        };

        if (state) {
          updateData.state = state;
        }
        if (adminNote && adminNote.trim()) {
          updateData.adminNote = adminNote.trim();
        } else if (adminNote !== undefined) {
          // Allow clearing admin note by sending null
          updateData.adminNote = null;
        }

        await updateUserReport(updateData);
      }

      if (onUpdate) {
        onUpdate();
      }
      toggle();
    } catch (error) {
      console.error("Error batch updating reports:", error);
      alert(error.response?.data?.message || error.message || "Failed to batch update reports");
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <form onSubmit={handleSubmit}>
        <ModalHeader toggle={toggle}>
          <i className="mdi mdi-checkbox-multiple-marked me-2"></i>
          Batch Update Reports
        </ModalHeader>
        <ModalBody>
          <p>
            Updating <Badge color="primary">{selectedReports?.length || 0}</Badge> selected reports.
          </p>
          
          {loading && progress.total > 0 && (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-muted">Progress:</small>
                <small className="text-muted">
                  {progress.current} / {progress.total}
                </small>
              </div>
              <div className="progress" style={{ height: "8px" }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          <FormGroup className="mb-3">
            <Label for="batchState">Set State {!adminNote && <span className="text-danger">*</span>}</Label>
            <Input
              type="select"
              id="batchState"
              value={state}
              onChange={(e) => setState(e.target.value)}
            >
              <option value="">-- Keep Current --</option>
              <option value="raised">Raised</option>
              <option value="inProgress">In Progress</option>
              <option value="resolved">Resolved</option>
            </Input>
          </FormGroup>

          <FormGroup>
            <Label for="batchAdminNote">Admin Note {!state && <span className="text-danger">*</span>}</Label>
            <Input
              type="textarea"
              id="batchAdminNote"
              rows="4"
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Enter admin note to apply to all selected reports..."
            />
          </FormGroup>

          <div className="alert alert-info mb-0">
            <small>
              <i className="mdi mdi-information me-1"></i>
              At least one field (State or Admin Note) must be provided.
            </small>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            type="submit" 
            disabled={loading || (!state && !adminNote.trim())}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Updating... ({progress.current}/{progress.total})
              </>
            ) : (
              <>
                <i className="mdi mdi-check me-2"></i>
                Update All
              </>
            )}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default BatchUpdateUserReportsModal;

